package com.siriusxm.unifiedcontent.console.player

import androidx.compose.runtime.Composable
import com.siriusxm.pia.components.SVGIcons
import com.siriusxm.pia.components.svgIcon
import com.siriusxm.pia.player.AudioTrack
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/** A keyboard-accessible transport action with its label owned by the button rather than the SVG. */
@Composable
internal fun PlayerIconButton(
    label: String,
    enabled: Boolean = true,
    className: String? = null,
    onClick: () -> Unit,
    content: @Composable () -> Unit,
) {
    Button({
        classes(AudioPlayerStyles.iconButton)
        className?.let { classes(it) }
        attr("type", "button")
        attr("aria-label", label)
        title(label)
        if (!enabled) attr("disabled", "")
        onClick { onClick() }
    }) { content() }
}

/** Artwork always fills a square source/cut tile; absent artwork uses the existing player glyph. */
@Composable
internal fun PlayerArtwork(url: String?, className: String) {
    Div({ classes(className) }) {
        if (url != null) {
            Img(url, alt = "", attrs = {
                style { width(100.percent); height(100.percent); property("object-fit", "cover") }
            })
        } else {
            svgIcon(SVGIcons.playerPlay) { size = 28.px }
        }
    }
}

/** The same accessible disclosure action appears in the expanded panel or beside the closed bar. */
@Composable
internal fun PlayerDetailsToggle(expanded: Boolean, onClick: () -> Unit) {
    Button({
        classes(AudioPlayerStyles.iconButton)
        if (expanded) classes(AudioPlayerStyles.collapseButton)
        attr("type", "button")
        attr("aria-label", if (expanded) "Hide playback details" else "Show playback details")
        attr("aria-expanded", expanded.toString())
        attr("aria-controls", "player-playback-details")
        onClick { onClick() }
    }) {
        svgIcon(SVGIcons.chevronUp) {
            size = 24.px
            if (expanded) style { property("transform", "rotate(180deg)") }
        }
    }
}

/** A recoverable playback failure stays above the source/transport rather than replacing its title. */
@Composable
internal fun PlaybackWarning(message: String, isLive: Boolean, onRetry: () -> Unit) {
    Div({ classes(AudioPlayerStyles.warning); attr("role", "alert") }) {
        Span({ classes(AudioPlayerStyles.warningIcon); attr("aria-hidden", "true") }) {
            svgIcon(SVGIcons.alertTriangle) { size = 30.px }
        }
        Span({ title(message) }) { Text(if (isLive) "This channel is temporarily unavailable." else message) }
        Button({ classes(AudioPlayerStyles.retryButton); attr("type", "button"); onClick { onRetry() } }) {
            Text("Retry")
        }
    }
}

/** Source context is independent of the currently playing cut and never invents a channel number. */
internal fun sourceCaption(source: AudioTrack): String =
    listOfNotNull(source.subTitle, if (source.isLive) "Live radio" else "On demand").joinToString(" · ")
